/**
 * Business Closed Dates Service
 *
 * Provides unified interface for managing and checking business closure status.
 * This service is the SINGLE SOURCE OF TRUTH for determining if a business is closed on any date.
 *
 * It consolidates:
 * - Weekly schedule closures (business_opening_hours.is_closed_all_day)
 * - Custom closed dates (business_closed_dates table)
 * - Public holidays (optional, based on business settings)
 */

import { supabase } from '@/integrations/supabase/client';
import { BusinessClosedDate, DateClosureStatus, BusinessOpeningHour, ClosureType } from '@/types';
import { format, getDay } from 'date-fns';

// Day name mapping for weekly schedule lookup
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Holiday interface (simplified for this service)
interface Holiday {
  date: string;
  name: string;
  is_non_working_day: boolean;
}

/**
 * Fetch all closed dates for a business within a date range
 */
export async function getClosedDatesForRange(
  businessId: string,
  startDate: Date,
  endDate: Date
): Promise<BusinessClosedDate[]> {
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');

  const { data, error } = await supabase
    .from('business_closed_dates')
    .select('*')
    .eq('business_id', businessId)
    .gte('closed_date', startDateStr)
    .lte('closed_date', endDateStr)
    .order('closed_date');

  if (error) {
    console.error('Error fetching closed dates:', error);
    return [];
  }

  return data || [];
}

/**
 * Check if a specific date is a custom closed date
 */
export async function isCustomClosedDate(
  businessId: string,
  date: Date
): Promise<BusinessClosedDate | null> {
  const dateStr = format(date, 'yyyy-MM-dd');

  const { data, error } = await supabase
    .from('business_closed_dates')
    .select('*')
    .eq('business_id', businessId)
    .eq('closed_date', dateStr)
    .maybeSingle();

  if (error) {
    console.error('Error checking closed date:', error);
    return null;
  }

  return data || null;
}

/**
 * Unified function to check if business is closed on a specific date (ASYNC version)
 *
 * Checks all closure sources in order of precedence:
 * 1. Custom closed dates (highest precedence - overrides weekly schedule)
 * 2. Weekly schedule (is_closed_all_day for day of week)
 * 3. Public holidays (optional, if business is configured to close on holidays)
 */
export async function getDateClosureStatus(
  businessId: string,
  date: Date,
  openingHours: BusinessOpeningHour[] | null | undefined,
  options?: {
    checkPublicHolidays?: boolean;
    countryCode?: string;
    holidays?: Holiday[];
    autoCloseOnHolidays?: boolean;
  }
): Promise<DateClosureStatus> {
  const dateStr = format(date, 'yyyy-MM-dd');
  const dayOfWeek = getDay(date);
  const dayName = DAY_NAMES[dayOfWeek];

  // 1. Check custom closed dates first (highest precedence)
  const customClosedDate = await isCustomClosedDate(businessId, date);
  if (customClosedDate) {
    return {
      isClosed: true,
      closureType: 'custom_closed_date',
      reason: customClosedDate.reason || 'Business closed',
      closedDateInfo: customClosedDate
    };
  }

  // 2. Check weekly schedule
  if (openingHours && openingHours.length > 0) {
    const dayHours = openingHours.find(oh => oh.day_of_week === dayName);
    if (dayHours?.is_closed_all_day) {
      return {
        isClosed: true,
        closureType: 'weekly_schedule',
        reason: `Closed on ${dayName}s`,
        closedDateInfo: null
      };
    }
  }

  // 3. Check public holidays (if enabled)
  if (options?.autoCloseOnHolidays && options?.holidays) {
    const holiday = options.holidays.find(h => h.date === dateStr && h.is_non_working_day);
    if (holiday) {
      return {
        isClosed: true,
        closureType: 'public_holiday',
        reason: holiday.name,
        closedDateInfo: null
      };
    }
  }

  // Not closed
  return {
    isClosed: false,
    closureType: null,
    reason: null,
    closedDateInfo: null
  };
}

/**
 * Synchronous version for components that already have data loaded
 * This is the preferred version for UI components with pre-fetched data
 */
export function getDateClosureStatusSync(
  date: Date,
  openingHours: BusinessOpeningHour[] | null | undefined,
  closedDates: BusinessClosedDate[],
  options?: {
    holidays?: Holiday[];
    autoCloseOnHolidays?: boolean;
  }
): DateClosureStatus {
  const dateStr = format(date, 'yyyy-MM-dd');
  const dayOfWeek = getDay(date);
  const dayName = DAY_NAMES[dayOfWeek];

  // 1. Check custom closed dates first (highest precedence)
  const customClosedDate = closedDates.find(cd => cd.closed_date === dateStr);
  if (customClosedDate) {
    return {
      isClosed: true,
      closureType: 'custom_closed_date',
      reason: customClosedDate.reason || 'Business closed',
      closedDateInfo: customClosedDate
    };
  }

  // 2. Check weekly schedule
  if (openingHours && openingHours.length > 0) {
    const dayHours = openingHours.find(oh => oh.day_of_week === dayName);
    if (dayHours?.is_closed_all_day) {
      return {
        isClosed: true,
        closureType: 'weekly_schedule',
        reason: `Closed on ${dayName}s`,
        closedDateInfo: null
      };
    }
  }

  // 3. Check public holidays (if enabled)
  if (options?.autoCloseOnHolidays && options?.holidays) {
    const holiday = options.holidays.find(h => h.date === dateStr && h.is_non_working_day);
    if (holiday) {
      return {
        isClosed: true,
        closureType: 'public_holiday',
        reason: holiday.name,
        closedDateInfo: null
      };
    }
  }

  // Not closed
  return {
    isClosed: false,
    closureType: null,
    reason: null,
    closedDateInfo: null
  };
}

/**
 * Add a closed date for a business
 */
export async function addClosedDate(
  businessId: string,
  closedDate: Date,
  reason?: string
): Promise<BusinessClosedDate> {
  const dateStr = format(closedDate, 'yyyy-MM-dd');

  const { data: userData } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('business_closed_dates')
    .insert({
      business_id: businessId,
      closed_date: dateStr,
      reason: reason || null,
      created_by: userData?.user?.id || null
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding closed date:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Update a closed date reason
 */
export async function updateClosedDate(
  id: string,
  reason: string
): Promise<BusinessClosedDate> {
  const { data, error } = await supabase
    .from('business_closed_dates')
    .update({ reason })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating closed date:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Remove a closed date
 */
export async function removeClosedDate(id: string): Promise<void> {
  const { error } = await supabase
    .from('business_closed_dates')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error removing closed date:', error);
    throw new Error(error.message);
  }
}

/**
 * Get existing shifts for a date (to check before marking as closed)
 */
export async function getShiftsForDate(
  businessId: string,
  date: Date
): Promise<{ count: number; hasAssignedShifts: boolean }> {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  const { data, error } = await supabase
    .from('shifts')
    .select('id, employee_id')
    .eq('business_id', businessId)
    .gte('start_time', startOfDay.toISOString())
    .lt('start_time', endOfDay.toISOString());

  if (error) {
    console.error('Error checking shifts for date:', error);
    return { count: 0, hasAssignedShifts: false };
  }

  const shiftsArray = data || [];
  return {
    count: shiftsArray.length,
    hasAssignedShifts: shiftsArray.some(s => s.employee_id !== null)
  };
}

/**
 * Batch check multiple dates for closure status
 * Useful for calendar month views
 */
export function getClosedDatesMap(
  closedDates: BusinessClosedDate[]
): Map<string, BusinessClosedDate> {
  const map = new Map<string, BusinessClosedDate>();
  closedDates.forEach(cd => {
    map.set(cd.closed_date, cd);
  });
  return map;
}

/**
 * Check if a date range contains any closed dates
 */
export function hasClosedDatesInRange(
  closedDates: BusinessClosedDate[],
  startDate: Date,
  endDate: Date
): boolean {
  const startStr = format(startDate, 'yyyy-MM-dd');
  const endStr = format(endDate, 'yyyy-MM-dd');

  return closedDates.some(cd =>
    cd.closed_date >= startStr && cd.closed_date <= endStr
  );
}

/**
 * Get auto_close_on_public_holidays setting for a business
 */
export async function getAutoCloseOnHolidaysSetting(
  businessId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('businesses')
    .select('auto_close_on_public_holidays')
    .eq('id', businessId)
    .single();

  if (error) {
    console.error('Error fetching auto close setting:', error);
    return false;
  }

  return data?.auto_close_on_public_holidays ?? false;
}

/**
 * Update auto_close_on_public_holidays setting for a business
 */
export async function updateAutoCloseOnHolidaysSetting(
  businessId: string,
  autoClose: boolean
): Promise<void> {
  const { error } = await supabase
    .from('businesses')
    .update({ auto_close_on_public_holidays: autoClose })
    .eq('id', businessId);

  if (error) {
    console.error('Error updating auto close setting:', error);
    throw new Error(error.message);
  }
}
